import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'; // this is needed!
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { AudioProjectModule } from '@components/sessions/single/audio-project/audio-project.module';
import { EvidenceFactsListComponent } from '@components/sessions/single/evidence-facts-list/evidence-facts-list.component';
import { NewIdeaModalComponent } from '@components/sessions/single/new-idea-modal/new-idea-modal.component';
import { VideoProjectModule } from '@components/sessions/single/video-project/video-project.module';
import { SharedModule } from '@components/shared/shared.module';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
// External Modules (dependencies)
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { OpenFeature } from '@openfeature/angular-sdk';
import { AssetsPipe } from '@pipes/assets.pipe';
import { FilterUsedCitationsPipe } from '@pipes/idea/filter-used-citations.pipe';
import { ParseUsedEvidence } from '@pipes/parse-used-evidence.pipe';
import { FormatSecondsToMinutesPipe } from '@pipes/time/format-seconds-to-minutes.pipe';
import { FeatureModule } from '@services/feature.service';
import { PodcastService } from '@services/sessions/podcast.service';
import { PODCAST_API_HTTP_INTERCEPTORS } from '@services/sessions/podcast-api.http-client';
import { MentionModule } from 'angular-mentions';
import { TagCloudComponent } from 'angular-tag-cloud-module';
import { AngularDraggableModule } from 'angular2-draggable';
import { TooltipModule } from 'ng2-tooltip-directive';
// TODO: find replacement
// import {TextInputHighlightModule} from 'angular-text-input-highlight';
import { TagInputModule } from 'ngx-chips';
import { CookieService } from 'ngx-cookie-service';
import { NgxEchartsModule } from 'ngx-echarts';

// Components
import { AppComponent } from './app.component';
import { appConfig } from './app.config';
// Internal Modules
import { AppRoutingModule } from './app.routes';
import { AuthLayoutComponent } from './components/account/auth-layout/auth-layout.component';
import { ChangePasswordComponent } from './components/account/change-password/change-password.component';
import { DirectMsgComponent } from './components/account/direct-msg/direct-msg.component';
import { ForgotPasswordComponent } from './components/account/forgot-password/forgot-password.component';
import { InviteDialogComponent } from './components/account/invite-dialog/invite-dialog.component';
import { LoginComponent } from './components/account/login/login.component';
import { NameTagComponent } from './components/account/profile/name-tag/name-tag.component';
import { ProfileComponent } from './components/account/profile/profile.component';
import { ProfilePictureComponent } from './components/account/profile/profile-picture/profile-picture.component';
import { ProfileViewComponent } from './components/account/profile/view/view.component';
import { RevokeInviteDialogComponent } from './components/account/revoke-invite-dialog/revoke-invite-dialog.component';
import { SignedUpComponent } from './components/account/signed-up/signed-up.component';
import { SignupComponent } from './components/account/signup/signup.component';
import { VerifyEmailComponent } from './components/account/verify-email/verify-email.component';
import { AdminUserSelectComponent } from './components/admin/admin-user-select/admin-user-select.component';
import { SessionWorkflowComponent } from './components/admin/session-workflow/session-workflow.component';
import { SessionsNotifEmailsComponent } from './components/admin/sessions-notif-emails/sessions-notif-emails.component';
import { AdminUserListComponent } from './components/admin/users/user-list.component';
import { ShortContentComponent } from './components/common/short-content/short-content.component';
import { GroupchatComponent } from './components/groupchat/groupchat.component';
import { GroupchatButtonComponent } from './components/groupchat/groupchat-button/groupchat-button.component';
import { HomeComponent } from './components/home/home.component';
import { IdeaRowComponent } from './components/home/idea-row/idea-row.component';
import { MainComponent } from './components/main/main.component';
import { ProsemirrorModule } from './components/misc/prosemirror/prosemirror.module';
import { TermsModalComponent } from './components/misc/terms-modal/terms-modal.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { D3WordCloudComponent } from './components/peoplefinder/d3-word-cloud/d3-word-cloud.component';
import { ExpertiseWordCloudComponent } from './components/peoplefinder/expertise-word-cloud/expertise-word-cloud.component';
import { FilteredIdeaListComponent } from './components/peoplefinder/filtered-idea-list/filtered-idea-list.component';
import { FilteredResultsToggleComponent } from './components/peoplefinder/filtered-results-toggle/filtered-results-toggle.component';
import { FilteredUsersPreviewComponent } from './components/peoplefinder/filtered-users-preview/filtered-users-preview.component';
import { PeoplefinderComponent } from './components/peoplefinder/peoplefinder.component';
import { SelectedUserComponent } from './components/peoplefinder/selected-user/selected-user.component';
import { TopicFilterComponent } from './components/peoplefinder/topic-filter/topic-filter.component';
import { UserFilterComponent } from './components/peoplefinder/user-filter/user-filter.component';
import { UserLocationFilterComponent } from './components/peoplefinder/user-location-filter/user-location-filter.component';
import { WordCloudFilterComponent } from './components/peoplefinder/word-cloud-filter/word-cloud-filter.component';
import { SearchComponent } from './components/search/search.component';
import { SessionDetailComponent } from './components/sessions/single/detail/detail.component';
import { IdeaDetailsComponent } from './components/sessions/single/idea/idea.component';
import { IdeaBarComponent } from './components/sessions/single/idea-bar/idea-bar.component';
import { IdeaBarControlsComponent } from './components/sessions/single/idea-bar/idea-bar-controls/idea-bar-controls.component';
import { SessionIdeaCardComponent } from './components/sessions/single/idea-card/idea-card.component';
import { AddCollaboratorModalComponent } from './components/sessions/single/idea-modal/add-collaborator-modal/add-collaborator-modal.component';
import { CollabInviteModalComponent } from './components/sessions/single/idea-modal/collab-invite-modal/collab-invite-modal.component';
import { CommentComponent } from './components/sessions/single/idea-modal/comment/comment.component';
import { CommentListComponent } from './components/sessions/single/idea-modal/comment-list/comment-list.component';
import { IdeaModalComponent } from './components/sessions/single/idea-modal/idea-modal.component';
import { IdeaMovesModalComponent } from './components/sessions/single/idea-modal/idea-moves-modal/idea-moves-modal.component';
import { SuggestCollabModalComponent } from './components/sessions/single/idea-modal/suggest-collab-modal/suggest-collab-modal.component';
import { IdeaPickerComponent } from './components/sessions/single/idea-picker/idea-picker.component';
import { MissingFactsListComponent } from './components/sessions/single/missing-facts-list/missing-facts-list.component';
import { SessionModulesControlCenterComponent } from './components/sessions/single/modules/control-center/control-center.component';
import { ExportWizardModalComponent } from './components/sessions/single/modules/control-center/export-wizard/modal.component';
import { IdeationModuleComponent } from './components/sessions/single/modules/control-center/ideation-module/ideation-module.component';
import { ImportWizardModalComponent } from './components/sessions/single/modules/control-center/import-wizard/modal.component';
import { SessionModulesIdeaBoardComponent } from './components/sessions/single/modules/idea-board/idea-board.component';
import { SessionIdeaCardSmallComponent } from './components/sessions/single/modules/idea-board/idea-card-small/idea-card-small.component';
import { SessionModulesIdeasMatrixComponent } from './components/sessions/single/modules/ideas-matrix/ideas-matrix.component';
import { SessionModulesMorphBoxComponent } from './components/sessions/single/modules/morph-box/morph-box.component';
import { NewMorphboxElementModalComponent } from './components/sessions/single/modules/morph-box/new-element-modal/new-element-modal.component';
import { NewMorphboxRowModalComponent } from './components/sessions/single/modules/morph-box/new-row-modal/new-row-modal.component';
import { ParticipantBarComponent } from './components/sessions/single/modules/people-module/participant-bar/participant-bar.component';
import { ParticipantBarControlComponent } from './components/sessions/single/modules/people-module/participant-bar/participant-bar-control/participant-bar-control.component';
import { PeopleModuleComponent } from './components/sessions/single/modules/people-module/people-module.component';
import { SessionModulesPersonalResultsComponent } from './components/sessions/single/modules/personal-results/personal-results.component';
import { SessionModulesRandomImagesComponent } from './components/sessions/single/modules/random-images/random-images.component';
import { SessionModulesRandomWordsComponent } from './components/sessions/single/modules/random-words/random-words.component';
import { SessionDetailsComponent } from './components/sessions/single/modules/session-details/session-details.component';
import { AddParticipantModalComponent } from './components/sessions/single/modules/session-settings/add-participant-modal/add-participant-modal.component';
import { SessionSettingsComponent } from './components/sessions/single/modules/session-settings/session-settings.component';
import { SessionModulesTeamPageComponent } from './components/sessions/single/modules/team-page/team-page.component';
import { MoveIdeasModalComponent } from './components/sessions/single/move-ideas-modal/move-ideas-modal.component';
import { PdfTaskComponent } from './components/sessions/single/pdf-task-modal/pdf-task/pdf-task.component';
import { PdfTaskModalComponent } from './components/sessions/single/pdf-task-modal/pdf-task-modal.component';
import { ProblemsListComponent } from './components/sessions/single/problems-list/problems-list.component';
import { SessionsSingleComponent } from './components/sessions/single/session.component';
import { ShotsListComponent } from './components/sessions/single/shots-list/shots-list.component';
import { NavItemComponent } from './components/sessions/single/side-nav/nav-item/nav-item.component';
import { SideNavComponent } from './components/sessions/single/side-nav/side-nav.component';
import { UserFeedbackComponent } from './components/sessions/single/user-feedback/user-feedback.component';
import { SessionsTopicsHeaderComponent } from './components/sessions/topics/header/header.component';
import { NewTopicModalComponent } from './components/sessions/topics/new-topic-modal/new-topic-modal.component';
import { NewTopicTypeModalComponent } from './components/sessions/topics/new-topic-type-modal/new-topic-type-modal.component';
import { PdfSelectionModalComponent } from './components/sessions/topics/pdf-selection-modal/pdf-selection-modal.component';
import { SessionCardComponent } from './components/sessions/topics/session-card/session-card.component';
import { TopicTypeSwitcherComponent } from './components/sessions/topics/topic-type-switcher/topic-type-switcher.component';
import { SessionsTopicsComponent } from './components/sessions/topics/topics.component';
import { TiptapEditorComponent } from './components/tiptap/tiptap-editor/tiptap-editor.component';
import { ClusterManagerModalComponent } from './components/topicviz-panel/cluster-manager-modal/cluster-manager-modal.component';
import { DatasetListComponent } from './components/topicviz-panel/cluster-manager-modal/dataset-list/dataset-list.component';
import { GenerateNewClusterComponent } from './components/topicviz-panel/cluster-manager-modal/generate-new-cluster/generate-new-cluster.component';
import { ImportDatasetComponent } from './components/topicviz-panel/cluster-manager-modal/import-dataset/import-dataset.component';
import { SyncIdeasComponent } from './components/topicviz-panel/cluster-manager-modal/sync-ideas/sync-ideas.component';
import { EditClusterModalComponent } from './components/topicviz-panel/edit-cluster-modal/edit-cluster-modal.component';
import { ExternalDocumentModalComponent } from './components/topicviz-panel/external-document-modal/external-document-modal.component';
import { SearchPixabayPictureModalComponent } from './components/topicviz-panel/search-pixabay-picture-modal/search-pixabay-picture-modal.component';
import { TopicvizPanelComponent } from './components/topicviz-panel/topicviz-panel.component';
import { ControlStatusDirective } from './directives/control-status.directive';
import { AdminGuard } from './guards/admin.guard';
// Guards
import { AnonymousGuard } from './guards/anonymous.guard';
import { AuthenticationGuard } from './guards/authentication.guard';
import { CanDeactivateGuard } from './guards/can-decativate.guard';
// Interceptors
import { AuthorizationInterceptor } from './interceptors/authorization.interceptor';
import { PodcastApiBaseUrlInterceptor } from './interceptors/podcast-api-base-url.interceptor';
import { PodcastApiBodyInterceptor } from './interceptors/podcast-api-body.interceptor';
import { PodcastApiRetryInterceptor } from './interceptors/podcast-api-retry.interceptor';
import { HideCitationsPipe } from './pipes/hide-citations.pipe';
import { AdminUserRequestService } from './services/admin/users.request.service';
import { AuthenticationRequestService } from './services/auth/authentication.request.service';
// Services
import { AuthenticationService } from './services/auth/authentication.service';
import { NotificationRequestService } from './services/auth/notification.request.service';
import { VerifyEmailService } from './services/auth/verify-email.service';
import { BusService } from './services/bus.service';
import { ConfigService } from './services/config.service';
import { EventsService } from './services/events.service';
import { FileUploadService } from './services/fileUpload.service';
import { GroupChatService } from './services/groupchat/groupchat.service';
import { RequestService } from './services/request.service';
import { SearchService } from './services/search.service';
import { IdeaRequestService } from './services/sessions/idea.request.service';
import { IdeaFromDocService } from './services/sessions/idea-from-doc.service';
import { PdfReportImageService } from './services/sessions/images.pdfreport.service';
import { MoveIdeasService } from './services/sessions/move-ideas.service';
import { PdfReportService } from './services/sessions/pdfreport.service';
import { SessionMorphboxRequestService } from './services/sessions/session.morphbox.request.service';
import { SessionWorkflowService } from './services/sessions/session.workflow.service';
import { SessionsRequestService } from './services/sessions/sessions.request.service';
import { GroupChatSocketService } from './services/sockets/groupchat.socket.service';
import { IdeaSocketService } from './services/sockets/idea.socket.service';
import { SessionSocketService } from './services/sockets/session.socket.service';
import { SessionsSocketService } from './services/sockets/sessions.socket.service';
import { UserSocketService } from './services/sockets/user.socket.service';
import { DirectMsgService } from './services/users/direct-msg.service';
import { UserInviteService } from './services/users/invite.service';
import { UsersService } from './services/users/users.service';

const appInitializer = function appInitializer(
  authService: AuthenticationService,
) {
  return () => {
    authService.getCurrentUser.subscribe(async ({ email }) => {
      await OpenFeature.setContext({ user: { email } });
    });
  };
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    MainComponent,
    NotFoundComponent,
    SignupComponent,
    SessionsTopicsComponent,
    SessionsTopicsHeaderComponent,
    NewTopicModalComponent,
    TermsModalComponent,
    SessionsSingleComponent,
    SessionModulesTeamPageComponent,
    SessionModulesRandomWordsComponent,
    SessionModulesRandomImagesComponent,
    SessionModulesPersonalResultsComponent,
    SessionModulesMorphBoxComponent,
    SessionModulesIdeasMatrixComponent,
    SessionModulesIdeaBoardComponent,
    SessionIdeaCardComponent,
    SessionDetailComponent,
    IdeaModalComponent,
    IdeaDetailsComponent,
    IdeationModuleComponent,
    NewMorphboxRowModalComponent,
    NewMorphboxElementModalComponent,
    SessionIdeaCardSmallComponent,
    AdminUserListComponent,
    PdfSelectionModalComponent,
    ExportWizardModalComponent,
    ImportWizardModalComponent,
    SessionsNotifEmailsComponent,
    AddCollaboratorModalComponent,
    HomeComponent,
    IdeaRowComponent,
    ChangePasswordComponent,
    ForgotPasswordComponent,
    PdfTaskModalComponent,
    PdfTaskComponent,
    SessionCardComponent,
    SearchComponent,
    ProfileComponent,
    CommentComponent,
    CommentListComponent,
    SuggestCollabModalComponent,
    MoveIdeasModalComponent,
    IdeaPickerComponent,
    SessionWorkflowComponent,
    ProfileViewComponent,
    DirectMsgComponent,
    PeopleModuleComponent,
    VerifyEmailComponent,
    SignedUpComponent,
    CollabInviteModalComponent,
    GroupchatComponent,
    GroupchatButtonComponent,
    NewTopicTypeModalComponent,
    TopicTypeSwitcherComponent,
    IdeaMovesModalComponent,
    AddParticipantModalComponent,
    InviteDialogComponent,
    RevokeInviteDialogComponent,
    SideNavComponent,
    NavItemComponent,
    SessionDetailsComponent,
    IdeaBarComponent,
    IdeaBarControlsComponent,
    PeoplefinderComponent,
    UserFilterComponent,
    SelectedUserComponent,
    UserLocationFilterComponent,
    FilteredIdeaListComponent,
    ExpertiseWordCloudComponent,
    WordCloudFilterComponent,
    TopicFilterComponent,
    D3WordCloudComponent,
    FilteredUsersPreviewComponent,
    FilteredResultsToggleComponent,
    TopicvizPanelComponent,
    ClusterManagerModalComponent,
    AdminUserSelectComponent,
    EditClusterModalComponent,
    SearchPixabayPictureModalComponent,
    GenerateNewClusterComponent,
    ParticipantBarComponent,
    ParticipantBarControlComponent,
    ExternalDocumentModalComponent,
    DatasetListComponent,
    ImportDatasetComponent,
    SyncIdeasComponent,
    ControlStatusDirective,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    NgbModule,
    TagInputModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    NgxSliderModule,
    TooltipModule,
    AngularDraggableModule,
    MentionModule,
    LeafletModule,
    NgSelectModule,
    VirtualScrollerModule,
    TagCloudComponent,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    TiptapEditorComponent,
    NewIdeaModalComponent,
    ParseUsedEvidence,
    EvidenceFactsListComponent,
    FilterUsedCitationsPipe,
    MissingFactsListComponent,
    ProblemsListComponent,
    UserFeedbackComponent,
    ShortContentComponent,
    CdkTextareaAutosize,
    HideCitationsPipe,
    ProfilePictureComponent,
    NameTagComponent,
    AuthLayoutComponent,
    ShotsListComponent,
    SharedModule,
    ProsemirrorModule,
    SessionSettingsComponent,
    SessionModulesControlCenterComponent,
    AssetsPipe,
    FormatSecondsToMinutesPipe,
    FeatureModule,
    AudioProjectModule,
    VideoProjectModule,
  ],
  providers: [
    AssetsPipe,
    AnonymousGuard,
    AuthenticationGuard,
    AdminGuard,
    CookieService,
    AuthenticationService,
    BusService,
    EventsService,
    AuthenticationRequestService,
    RequestService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationInterceptor,
      multi: true,
    },
    {
      provide: PODCAST_API_HTTP_INTERCEPTORS,
      useClass: PodcastApiBaseUrlInterceptor,
      multi: true,
    },
    {
      provide: PODCAST_API_HTTP_INTERCEPTORS,
      useClass: PodcastApiBodyInterceptor,
      multi: true,
    },
    {
      provide: PODCAST_API_HTTP_INTERCEPTORS,
      useClass: PodcastApiRetryInterceptor,
      multi: true,
    },
    ConfigService,
    SessionsRequestService,
    FileUploadService,
    IdeaRequestService,
    SessionSocketService,
    IdeaSocketService,
    UserSocketService,
    GroupChatSocketService,
    SessionsSocketService,
    CanDeactivateGuard,
    SessionMorphboxRequestService,
    AdminUserRequestService,
    PdfReportImageService,
    PdfReportService,
    NotificationRequestService,
    SearchService,
    UsersService,
    DirectMsgService,
    MoveIdeasService,
    SessionWorkflowService,
    // GoogleAnalyticsEventsService,
    VerifyEmailService,
    GroupChatService,
    UserInviteService,
    IdeaFromDocService,
    provideHttpClient(withInterceptorsFromDi()),
    PodcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [AuthenticationService],
      multi: true,
    },
    ...appConfig.providers,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
